<!--
  AssistantEditor — the simplified, non-technical editor for one assistant. It is a LENS over a real
  AgentConfig: it reads via assistantFromAgent and writes via applyAssistantToAgent + the existing
  `setAgentConfig` action. Deep settings (memory/embeddings, model options) stay in Advanced.
  Built from reused parts: EditorBlock (Automations export) + BrainPicker.
-->
<template>
  <div>
    <!-- header -->
    <div class="d-flex align-center ga-3 mb-6">
      <v-btn icon="mdi-arrow-left" variant="text" @click="$emit('back')" />
      <div class="flex-grow-1" style="min-width: 0">
        <v-text-field
          v-model="draft.name"
          variant="plain"
          density="compact"
          hide-details
          placeholder="Assistant name"
          class="text-h5 font-weight-bold assistant-name"
        />
        <span class="text-caption text-medium-emphasis">{{ brainLabel ? `Runs on ${brainLabel}` : 'No brain selected yet' }}</span>
      </div>
      <v-tooltip
        :disabled="!saveDisabled"
        text="Name, description, instructions and a brain are required."
        location="top"
      >
        <template #activator="{ props }">
          <span v-bind="props">
            <v-btn color="primary" :loading="saving" :disabled="saveDisabled" @click="save">Save</v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>

    <!-- 1. What it does -->
    <EditorBlock n="1" title="What it does" hint="Describe what it should do, then the instructions it follows. With a name, description and brain set, the ⚡ writes the instructions from your description.">
      <!-- The agent's description — the same field Advanced has, and the input the ⚡ autofill
           generates the instructions from. Multi-line so there's room to describe properly. -->
      <v-textarea
        v-model="draft.desc"
        variant="outlined"
        density="compact"
        hide-details
        auto-grow
        rows="2"
        class="mb-4"
        placeholder="What should this assistant do? Add a brain below, then the ⚡ writes its instructions from this."
      />
      <v-textarea
        v-model="draft.instructions"
        :loading="autofilling"
        :disabled="autofilling"
        variant="outlined"
        density="compact"
        hide-details
        auto-grow
        rows="5"
        placeholder="How it should behave…"
      >
        <!-- The Advanced AgentCard's Autofill: generate the instructions from name + summary with
             the selected brain. Same visibility rule (name, description and model all set). -->
        <template #append-inner>
          <v-tooltip v-if="canAutofill" text="Autofill">
            <template #activator="{ props }">
              <v-icon v-bind="props" color="primary" icon="mdi-lightning-bolt-outline" class="autofill-icon" @click="autofillInstructions" />
            </template>
          </v-tooltip>
        </template>
      </v-textarea>
    </EditorBlock>

    <!-- 2. Its brain -->
    <EditorBlock n="2" title="Its brain" hint="Which model powers it. Keys live on the Providers tab.">
      <BrainPicker
        :model-value="selectedModelId"
        :models="modelConfigsList"
        @update:model-value="selectedModelId = $event"
      />
    </EditorBlock>

    <!-- 3. What it can do -->
    <EditorBlock n="3" title="What it can do" hint="Connect tools so it can take action.">
      <v-select
        v-model="draft.tools"
        :items="toolNames"
        variant="outlined"
        density="compact"
        hide-details
        multiple
        chips
        closable-chips
        placeholder="Add tools…"
      />
      <!-- Narrow each connected tool to specific actions (the Advanced "Select Functions" flow, reused). -->
      <div v-if="draft.tools.length" class="mt-2">
        <v-btn variant="text" size="small" class="ml-n2" prepend-icon="mdi-tune-variant" @click="functionsOpen = true">
          Choose specific actions ({{ selectedFunctionsCount }} selected)
        </v-btn>
        <FunctionSelectionDialog
          :show="functionsOpen"
          :selected-tool-names="draft.tools"
          :initial-selected-functions="selectedFunctions"
          @save="onFunctionsSave"
          @update:modelValue="functionsOpen = $event"
        />
      </div>
      <div class="text-caption text-medium-emphasis mt-3">
        Memory (documents): {{ draft.memory ? 'on' : 'off' }} — manage in Advanced.
      </div>
    </EditorBlock>

    <!-- 4. Voice -->
    <EditorBlock n="4" title="Voice" hint="Give it a voice so it can speak its replies aloud.">
      <v-select
        v-model="draft.voice"
        :items="voiceNames"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        placeholder="No voice"
      />
    </EditorBlock>

    <!-- 5. Publishing -->
    <EditorBlock n="5" title="Publishing" hint="Publish it so other people can use it.">
      <!-- Saved agent: publish is a live server-side toggle. Draft: capture the intent locally and
           publish it right after the create succeeds (matches the Advanced AgentCard, which also lets
           you publish while creating — see save()). -->
      <v-switch
        v-if="!isDraft"
        :model-value="published"
        color="primary"
        density="compact"
        hide-details
        :label="published ? 'Published' : 'Not published'"
        @update:model-value="togglePublish"
      />
      <v-switch
        v-else
        v-model="publishOnSave"
        color="primary"
        density="compact"
        hide-details
        :label="publishOnSave ? 'Will publish when saved' : 'Publish for others'"
      />

      <!-- Once it's live, surface the same publish cluster Advanced's AgentCard shows: share it (with
           per-recipient edit permission, via the reused AgentShareDialog), open its public page, message
           it, and copy its public link. Saved + published only — drafts have no publishedInfo yet. -->
      <template v-if="!isDraft && published">
        <div class="d-flex align-center flex-wrap ga-2 mt-4">
          <v-btn color="primary" variant="tonal" prepend-icon="mdi-share-variant" @click="showShare = true">
            Share
          </v-btn>
          <v-btn
            v-if="publicUrl"
            variant="text"
            prepend-icon="mdi-open-in-new"
            :href="publicUrl"
            target="_blank"
            rel="noopener"
          >
            Open public page
          </v-btn>
          <v-btn v-if="publishedSlug" variant="text" prepend-icon="mdi-chat-outline" @click="goToChat">
            Message it
          </v-btn>
        </div>
        <v-text-field
          v-if="publicUrl"
          :model-value="publicUrl"
          label="Public link"
          readonly
          variant="outlined"
          density="compact"
          hide-details
          class="mt-3"
          append-inner-icon="mdi-content-copy"
          @click:append-inner="copyPublicUrl"
        />
      </template>

      <AgentShareDialog
        v-if="!isDraft"
        v-model="showShare"
        :owner-username="username"
        :slug="publishedSlug"
        :agent-name="agent.name"
      />
    </EditorBlock>

    <!-- delete — its own section at the end, matching the Automations editor. Drafts have nothing
         to delete yet (Back simply discards them). -->
    <EditorBlock v-if="!isDraft" title="Delete" hint="Removes this assistant for good — it disappears from Advanced too.">
      <v-btn color="error" variant="tonal" prepend-icon="mdi-trash-can-outline" :loading="deleting" @click="confirmDelete = true">
        Delete this assistant
      </v-btn>
    </EditorBlock>

    <ConfirmationDialog
      v-model="confirmDelete"
      title="Delete assistant?"
      message="This deletes the real agent — it disappears from Advanced too."
      :item-name="agent.name"
      item-type="assistant"
      @confirm="onDeleteConfirm"
      @cancel="confirmDelete = false"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import EditorBlock from '@/components/agentpages/automations/EditorBlock.vue'
import BrainPicker from '@/components/agentpages/assistants/BrainPicker.vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'
import FunctionSelectionDialog from '@/components/dialogs/FunctionSelectionDialog.vue'
import AgentShareDialog from '@/components/AgentShareDialog.vue'
import { getDashboardTabPath } from '@/router/dashboardTabs'
import { assistantFromAgent, applyAssistantToAgent, resolveModelConfig, brainLabelForModel } from '@/utils/assistants'
import type { Assistant } from '@/types/assistants'
import type { AgentConfig } from '@/types/agents'
import type { AgentDefaults } from '@/store/agents'
import type { ModelConfig } from '@/types/models'

@Options({
  components: { EditorBlock, BrainPicker, ConfirmationDialog, FunctionSelectionDialog, AgentShareDialog },
  props: {
    agent: { type: Object, required: true }, // the real AgentConfig being edited
    isDraft: { type: Boolean, default: false } // a brand-new agent that hasn't been saved yet
  },
  emits: ['back', 'saved', 'deleted'],
  computed: {
    ...mapGetters(['modelConfigsList', 'mcpServers', 'voiceConfigs', 'agentDefaults', 'username'])
  }
})
export default class AssistantEditor extends Vue {
  agent!: AgentConfig
  isDraft!: boolean
  modelConfigsList!: ModelConfig[]
  mcpServers!: Record<string, unknown>
  voiceConfigs!: Record<string, unknown>
  agentDefaults!: AgentDefaults
  username!: string

  draft: Assistant = {} as Assistant
  selectedModelId = '' // the agent's model, resolved to a ModelConfig id (the picker is id-based)
  saving = false
  confirmDelete = false
  deleting = false
  autofilling = false
  publishOnSave = false // draft only: publish the agent immediately after its first save
  showShare = false // the reused AgentShareDialog (manage recipients + per-recipient edit access)
  functionsOpen = false // the reused FunctionSelectionDialog (per-server "specific actions" narrowing)
  // Per-server function narrowing, edited via FunctionSelectionDialog. Held locally (it is not part of
  // the Assistant lens type) and folded back into toolOptions.selectedFunctions on save.
  selectedFunctions: { [serverName: string]: string[] } = {}

  created() {
    this.resetDraft()
  }

  resetDraft() {
    this.draft = assistantFromAgent(this.agent)
    // AgentConfig.model may hold the model's id or (for older records) its name — normalise to the id.
    this.selectedModelId = resolveModelConfig(this.agent.model, this.modelConfigsList || [])?.id ?? ''
    // Seed the per-function narrowing from the real config (copy so edits stay local until save).
    this.selectedFunctions = { ...(this.agent.toolOptions?.selectedFunctions ?? {}) }
  }

  // Read from the live config, not a local copy: toggleAgentPublish re-fetches published agents on
  // success AND failure, and that fetch updates this config's publishedInfo in place — so the switch
  // always shows the server's truth (and self-corrects if the toggle call errors).
  get published(): boolean {
    return !!this.agent.publishedInfo?.isPublished
  }

  // The published page's URL and slug, read straight off the lens's live config (same source the
  // Advanced AgentCard uses). Empty until the agent is saved and published.
  get publicUrl(): string {
    return this.agent.publishedInfo?.publicUrl ?? ''
  }

  get publishedSlug(): string {
    return this.agent.publishedInfo?.slug ?? ''
  }

  // Same required fields as the Advanced editor (AgentCard.saveDisabled), so the two surfaces can't
  // drift: a nameless agent would also collide in the store, which keys configs by name.
  get saveDisabled(): boolean {
    return (
      !this.draft.name?.trim() ||
      !this.draft.desc?.trim() ||
      !this.draft.instructions?.trim() ||
      !this.selectedModelId
    )
  }

  // The currently-selected model (by id), used for the header label.
  get selectedModel(): ModelConfig | null {
    return resolveModelConfig(this.selectedModelId, this.modelConfigsList || [])
  }

  // Header subtitle: the curated tier when known, else the real model name, else a neutral fallback.
  get brainLabel(): string {
    return brainLabelForModel(this.selectedModel)
  }

  get toolNames(): string[] {
    return Object.keys(this.mcpServers || {})
  }

  get voiceNames(): string[] {
    return Object.keys(this.voiceConfigs || {})
  }

  // Count of narrowed functions across the currently-selected tools (ignores stale entries for tools
  // that were de-selected — those get pruned on save by buildAgentToolOptions).
  get selectedFunctionsCount(): number {
    return this.draft.tools.reduce((n, name) => n + (this.selectedFunctions[name]?.length || 0), 0)
  }

  onFunctionsSave(functions: { [serverName: string]: string[] }) {
    this.selectedFunctions = functions
  }

  // Mirrors AgentCard's Autofill visibility: name, description and a model must all be set.
  get canAutofill(): boolean {
    return !!this.draft.name && !!this.draft.desc && !!this.selectedModel && !this.autofilling
  }

  // The Advanced AgentCard's autofillPrompt, ported: substitute name/description into the
  // agentDefaults prompt template, generate with the selected model, strip <think> blocks.
  async autofillInstructions() {
    const model = this.selectedModel
    if (!model) return
    this.autofilling = true
    try {
      const { content } = this.agentDefaults.prompt
      if (typeof content !== 'string') {
        this.$store.dispatch('updateSnackbar', { text: 'Cannot autofill prompt with complex content.', color: 'error', timeout: 3000 })
        return
      }
      const prompt = content.replace('<name>', this.draft.name).replace('<description>', this.draft.desc)
      this.$store.dispatch('updateSnackbar', { text: `Sent autofill request for ${this.draft.name} description`, timeout: 3_000 })
      const response: string = await this.$store.dispatch('generatePrompt', { prompt, modelConfig: model })
      // The action returns this sentinel (plus its own error snackbar) on failure — don't write it
      // into the instructions a user might then save.
      if (response === 'Error generating prompt') return
      this.draft.instructions = response.replace(/<think>[\s\S]*?<\/think>/g, '').replace(/^\n+/, '')
    } finally {
      this.autofilling = false
    }
  }

  async save() {
    this.saving = true
    try {
      const updated = applyAssistantToAgent(this.draft, this.agent, this.selectedModelId || null, this.selectedFunctions)
      const payload: { config: AgentConfig; previousName?: string } = { config: updated }
      if (!this.isDraft) {
        payload.previousName = this.agent.name
      }
      const ok = await this.$store.dispatch('setAgentConfig', payload)
      if (!ok) return
      // Publish-on-create: setAgentConfig wrote the server-assigned id back into `updated`, so the
      // agent now exists and toggleAgentPublish (publish-by-name) resolves it. Only for a fresh draft
      // the user opted to publish; saved agents use the live switch above. A new manual agent is
      // unpublished, so a single toggle publishes it.
      if (this.isDraft && this.publishOnSave) {
        await this.$store.dispatch('toggleAgentPublish', { agentId: updated.id, agentName: updated.name })
      }
      this.$emit('saved')
    } finally {
      this.saving = false
    }
  }

  // Publishing is a server-side toggle (same action the Advanced agent card uses). No optimistic
  // local state — the `published` getter re-reads the config after the action's refresh.
  async togglePublish() {
    await this.$store.dispatch('toggleAgentPublish', { agentId: this.agent.id, agentName: this.agent.name })
  }

  // "Message it": hand off to the Chat tab with this published agent pre-selected. Mirrors
  // AgentCard.goToChat so Simple and Advanced reach the same chat the same way.
  goToChat(): void {
    if (!this.publishedSlug) return
    this.$store.dispatch('setPendingChatAgent', { slug: this.publishedSlug })
    this.$store.dispatch('setTab', 'chat')
    const path = getDashboardTabPath('chat')
    if (this.$route.path !== path) {
      this.$router.push(path)
    }
  }

  copyPublicUrl(): void {
    if (!this.publicUrl) return
    void navigator.clipboard.writeText(this.publicUrl)
    this.$store.dispatch('updateSnackbar', { text: 'Public link copied to clipboard!', timeout: 3000 })
  }

  // Same action the Advanced AgentCard deletes with (keyed by the agent's saved name — NOT the
  // draft's, which may hold an unsaved rename). The action snackbars its own errors.
  async onDeleteConfirm() {
    this.confirmDelete = false
    this.deleting = true
    try {
      const ok = await this.$store.dispatch('deleteAgentConfig', this.agent.name)
      if (ok) this.$emit('deleted')
    } finally {
      this.deleting = false
    }
  }
}
</script>

<style scoped lang="scss">
.assistant-name :deep(input) {
  font-size: 1.5rem;
  font-weight: 700;
}

.autofill-icon {
  cursor: pointer;
}
</style>
